import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Photo, PhotoAlbum, PhotoBook } from '@td/shared';
import { del, get, patch, post, postForm } from '../lib/api';

const KEY = ['photos'];

/** This year's book, or one that has been put away. */
export function usePhotoBook(year: string | null) {
  return useQuery({
    queryKey: [...KEY, year ?? 'current'],
    queryFn: () => get<PhotoBook>(`/photos${year ? `?year=${encodeURIComponent(year)}` : ''}`),
  });
}

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: KEY });
}

/**
 * Where the bytes come from.
 *
 * Plain URLs rather than fetched blobs, so the browser caches them and an
 * `<img>` can be printed. The session cookie goes with them like any other
 * same-origin request — these are photographs of children and the route
 * checks the owner on every single one.
 */
export function photoThumbUrl(photo: Photo): string {
  return `/api/photos/photo/${photo.id}/thumb`;
}

export function photoFullUrl(photo: Photo): string {
  return `/api/photos/photo/${photo.id}/file`;
}

export function photoDownloadUrl(photo: Photo): string {
  return `/api/photos/photo/${photo.id}/file?download`;
}

/** A zip of a month, an album, a selection, or a whole year. */
export function photoZipUrl(options: {
  year?: string | null;
  month?: string;
  albumId?: string;
  ids?: string[];
}): string {
  const params = new URLSearchParams();
  if (options.year) params.set('year', options.year);
  if (options.month) params.set('month', options.month);
  if (options.albumId) params.set('albumId', options.albumId);
  if (options.ids?.length) params.set('ids', options.ids.join(','));

  return `/api/photos/zip?${params.toString()}`;
}

export function useUploadPhoto() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ file, albumId }: { file: File; albumId?: string | null }) => {
      const body = new FormData();
      body.append('file', file);
      if (albumId) body.append('albumId', albumId);
      return postForm<Photo>('/photos', body);
    },
    onSuccess: refresh,
  });
}

export function useUpdatePhoto() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      title?: string;
      takenAt?: string;
      albumId?: string | null;
    }) => patch<Photo>(`/photos/photo/${id}`, input),
    onSuccess: refresh,
  });
}

export function useDeletePhoto() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => del(`/photos/photo/${id}`),
    onSuccess: refresh,
  });
}

export function useCreateAlbum() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (name: string) => post<PhotoAlbum>('/photos/albums', { name }),
    onSuccess: refresh,
  });
}

export function useRenameAlbum() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      patch<PhotoAlbum>(`/photos/album/${id}`, { name }),
    onSuccess: refresh,
  });
}

export function useDeleteAlbum() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => del(`/photos/album/${id}`),
    onSuccess: refresh,
  });
}
